import { RefObject, useEffect } from "react"

interface UseAutoFitEscalaParams {
    containerRef: RefObject<HTMLElement | null>
    contentRef: RefObject<HTMLElement | null>
    // Identifica el contenido: al cambiar se vuelve a calcular.
    id: string
    minEscala?: number
}

const TEXTOS = "p, strong"

// Reduce todos los textos del contenido por el mismo factor hasta que quepan en el contenedor.
// Parte del tamaño que da el CSS en el breakpoint activo, así que si ya cabe no cambia nada.
export function useAutoFitEscala({ containerRef, contentRef, id, minEscala = 0.3 }: UseAutoFitEscalaParams) {
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined = undefined

        const ajustar = () => {
            const container = containerRef.current
            const content = contentRef.current
            if (!container || !content) return

            // Se desconecta mientras se cambian estilos para no entrar en un ciclo con el observer.
            observer.disconnect()

            const textos = Array.from(content.querySelectorAll<HTMLElement>(TEXTOS))
            textos.forEach((el) => el.style.removeProperty("font-size"))
            const naturales = textos.map((el) => parseFloat(getComputedStyle(el).fontSize))

            const aplicar = (escala: number) =>
                textos.forEach((el, i) => {
                    el.style.fontSize = `${naturales[i] * escala}px`
                })
            const cabe = () =>
                content.scrollHeight <= container.clientHeight && content.scrollWidth <= container.clientWidth

            if (!cabe()) {
                let low = minEscala
                let high = 1
                let mejor = minEscala
                while (high - low > 0.01) {
                    const mid = (low + high) / 2
                    aplicar(mid)
                    if (cabe()) {
                        mejor = mid
                        low = mid
                    } else {
                        high = mid
                    }
                }
                aplicar(mejor)
            }

            observer.observe(container)
        }

        const ajustarConEspera = () => {
            clearTimeout(timer)
            timer = setTimeout(ajustar, 150)
        }

        const observer = new ResizeObserver(ajustarConEspera)

        const inicializar = async () => {
            if ("fonts" in document) {
                await document.fonts.ready
            }
            ajustar()
        }

        inicializar()

        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        return () => {
            clearTimeout(timer)
            observer.disconnect()
        }
    }, [id, minEscala, containerRef, contentRef])
}
